import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { Utils, authJsonHeaders, formatDateTime, formatDate, handleSessionExpired, getInvoiceStatusDisplay, getInvoiceKindDisplay, INVOICE_STATUS_CODES, INVOICE_KIND_CODES, isCheckoutInvoice, METRIC_TYPE_CODES, PLAN_TYPE_CODES, calculateGrowthPlanEstimate, DEFAULT_BILLING_CURRENCY, bigAmountFormat, parseInvoiceBreakdown, getDefaultPageSize, selectStyles, getBrowserTimeZone , formatMetricPrice} from '../../utils';
import BillView from '../billing/billView';
import DateRangePicker from '../common/DateRangePicker/DateRangePicker';
import { toast } from 'react-toastify';
import { MdSearch, MdRefresh, MdEdit, MdCheckCircle, MdVisibility, MdErrorOutline, MdVerified } from 'react-icons/md';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { useActionDropdown } from '../../hooks/useActionDropdown';
import Modal from '../common/Modal';
import ListPaginationBar from '../common/ListPaginationBar';

function invoicePaymentErrorText(inv) {
    const err = inv?.last_payment_error;
    if (!err) return '';
    return [
        err.error_reason || err.error_code || 'Auto-pay failed',
        err.error_description ? String(err.error_description) : null,
    ].filter(Boolean).join(': ');
}

function getInvoicePaymentAttempts(inv) {
    if (Array.isArray(inv?.payment_attempts) && inv.payment_attempts.length) {
        return inv.payment_attempts;
    }
    const fromBreakdown = inv?.usage_breakdown?.payment_attempts;
    return Array.isArray(fromBreakdown) ? fromBreakdown : [];
}

/** Pending usage invoice still in admin-review window (user_visible_at in future). */
function isAwaitingAdminApproval(row) {
    if (!row || isCheckoutInvoice(row) || Number(row.status) !== INVOICE_PENDING) return false;
    const at = row.user_visible_at ? new Date(row.user_visible_at).getTime() : NaN;
    return Number.isFinite(at) && at > Date.now();
}

function getInvoiceActionOptions(row, { onView, onMarkPaid, onEdit, onPaymentErrors, onApprove }) {
    const opts = [
        { key: 'view', label: 'View', icon: <MdVisibility size={18} />, action: () => onView(row) },
    ];
    if (row.has_payment_errors || Number(row.payment_attempts_count) > 0 || getInvoicePaymentAttempts(row).length > 0) {
        opts.push({
            key: 'payment_errors',
            label: 'Payment errors',
            icon: <MdErrorOutline size={18} color="#b91c1c" />,
            action: () => onPaymentErrors(row),
        });
    }
    if (isAwaitingAdminApproval(row)) {
        opts.push({
            key: 'show_to_user',
            label: 'Show to User',
            icon: <MdVerified size={18} color="#059669" />,
            action: () => onApprove(row),
        });
    }
    // Mark paid / Edit only while the usage bill is still pending (hidden once Paid/Cancelled)
    if (!isCheckoutInvoice(row) && Number(row.status) === INVOICE_PENDING) {
        opts.push({
            key: 'mark_paid',
            label: 'Mark paid',
            icon: <MdCheckCircle size={18} />,
            action: () => onMarkPaid(row),
        });
        opts.push({
            key: 'edit',
            label: 'Edit',
            icon: <MdEdit size={18} />,
            action: () => onEdit(row),
        });
    }
    return opts;
}

const INVOICE_PENDING = INVOICE_STATUS_CODES[0]?.value ?? 0;
const INVOICE_PAID    = INVOICE_STATUS_CODES[1]?.value ?? 1;

/** Status from shared INVOICE_STATUS_CODES (+ All). */
const INVOICE_STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    ...INVOICE_STATUS_CODES.map((s) => ({ value: s.value, label: s.label })),
];

const KIND_OPTIONS = [
    { value: 'all', label: 'All types' },
    ...INVOICE_KIND_CODES.map((s) => ({ value: s.value, label: s.label })),
];

const ERROR_OPTIONS = [
    { value: 'all', label: 'All invoices' },
    { value: 'errors', label: 'Payment errors only' },
];

const filterSelectStyles = {
    ...selectStyles,
    container: (base) => ({ ...base, minWidth: 180, width: 200 }),
    control: (base, state) => ({
        ...(selectStyles.control ? selectStyles.control(base, state) : base),
        minHeight: 38,
    }),
};

// ── Mark Paid Modal
const MarkPaidModal = ({ invoice, onClose, onSuccess }) => {
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}admin/users/invoice-status-set`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ invoiceId: invoice.id, status: INVOICE_PAID, notes }),
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) { toast.success(r.message || 'Invoice marked as paid'); onSuccess(); onClose(); }
            else toast.error(r.message || 'Could not update invoice');
        } catch (e) { toast.error(e?.message || 'Could not update invoice'); }
        setLoading(false);
    };

    return (
        <Modal title={`Mark Invoice #${invoice.id} as Paid`} onClose={onClose} maxWidth="640px"
            footer={<>
                <button type="button" onClick={onClose} style={{ padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button type="button" onClick={submit} disabled={loading} style={{ padding: '10px 18px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                    {loading ? 'Saving…' : 'Confirm — Mark Paid'}
                </button>
            </>}
        >
            {/* Invoice summary */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                    ['User', invoice.user?.name || invoice.user?.email || '—'],
                    ['Amount', bigAmountFormat(invoice.amount, invoice.currency_symbol)],
                    ['Plan', invoice.plan_name || '—'],
                    isCheckoutInvoice(invoice) ? null : ['Period', `${formatDate(invoice.period_start)} – ${formatDate(invoice.period_end)}`],
                ].filter(Boolean).map(([label, val]) => (
                    <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>{label}</div>
                        <strong style={{ fontSize: 13, color: '#111827', wordBreak: 'break-all' }}>{val}</strong>
                    </div>
                ))}
            </div>

            {/* Notes */}
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Payment notes (optional)
            </label>
            <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Received via NEFT on 24-06-2026, UTR: 123456"
                rows={3}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
            />
        </Modal>
    );
};

// ── Show-to-User Invoice Modal — reveal to app user now + email (does NOT charge/mark paid;
// payment still follows the normal auto-charge cron / manual Mark Paid flow separately).
const ApproveInvoiceModal = ({ invoice, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}admin/invoice/approve`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ invoiceId: invoice.id }),
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) { toast.success(r.message || 'Invoice is now visible to the user'); onSuccess(); onClose(); }
            else toast.error(r.message || 'Could not show invoice to user');
        } catch (e) { toast.error(e?.message || 'Could not show invoice to user'); }
        setLoading(false);
    };

    return (
        <Modal title={`Show Invoice #${invoice.id} to User`} onClose={onClose} maxWidth="560px"
            footer={<>
                <button type="button" onClick={onClose} style={{ padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button type="button" onClick={submit} disabled={loading} style={{ padding: '10px 18px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                    {loading ? 'Showing…' : 'Show to User'}
                </button>
            </>}
        >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                {[
                    ['User', invoice.user?.name || invoice.user?.email || '—'],
                    ['Amount', bigAmountFormat(invoice.amount, invoice.currency_symbol)],
                    ['Plan', invoice.plan_name || '—'],
                    isCheckoutInvoice(invoice) ? null : ['Period', `${formatDate(invoice.period_start)} – ${formatDate(invoice.period_end)}`],
                ].filter(Boolean).map(([label, val]) => (
                    <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>{label}</div>
                        <strong style={{ fontSize: 13, color: '#111827', wordBreak: 'break-all' }}>{val}</strong>
                    </div>
                ))}
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
                The invoice becomes visible to the user right away and they get the invoice email.
                This only reveals the invoice — payment is not charged here; it still follows the
                usual auto-charge (if a card is saved) or gets marked paid separately.
            </p>
        </Modal>
    );
};

// ── Edit Invoice Amount Modal
const EditAmountModal = ({ invoice, onClose, onSuccess }) => {
    const breakdown = parseInvoiceBreakdown(invoice.usage_breakdown);

    // initialise metric counts from invoice's own usageByType snapshot
    const initCounts = () => {
        const base = breakdown.usageByType || {};
        const result = {};
        (METRIC_TYPE_CODES || []).filter((m) => m.value > 0).forEach((m) => {
            result[m.value] = base[m.value] ?? base[String(m.value)] ?? 0;
        });
        return result;
    };

    // Wrapped in useMemo so this has a stable reference across renders (it's used as a
    // useEffect dependency below) rather than a new {} literal every render.
    const estimate = useMemo(() => breakdown.estimate || {}, [breakdown.estimate]);
    const currSym = invoice.currency_symbol || DEFAULT_BILLING_CURRENCY?.symbol;
    // Wrapped in useMemo so this array has a stable reference across renders (it's used as a
    // useEffect dependency below) rather than a new [] literal every render.
    const ratesApplied = useMemo(() => breakdown.rates_applied || [], [breakdown.rates_applied]);
    const billingCycle = breakdown.billing_cycle || 'monthly';

    const [counts, setCounts] = useState(initCounts);
    const [actualCounts, setActualCounts] = useState({});
    const [resolvedPricing, setResolvedPricing] = useState(ratesApplied);
    // Start with the invoice's saved subtotal (keeps an earlier admin override)
    const [overrideAmount, setOverrideAmount] = useState(() => {
        const saved = parseFloat(estimate.subtotal_before_tax ?? estimate.total ?? breakdown.subtotal_before_tax);
        return Number.isFinite(saved) ? String(Math.round(saved * 100) / 100) : '';
    });
    // Fixed base/plan price — editable on its own, separate from the per-metric usage lines below.
    // Starts from the invoice's own saved base (falls back to the plan's base rate once pricing loads).
    const [baseAmount, setBaseAmount] = useState(() => {
        const saved = parseFloat(estimate.base);
        return Number.isFinite(saved) ? saved : 0;
    });
    const [countsTouched, setCountsTouched] = useState(false);
    const [baseTouched, setBaseTouched] = useState(false);
    const [loading, setLoading] = useState(false);

    // Invoice snapshot rates, or public Growth pricing as fallback
    useEffect(() => {
        if (ratesApplied.length > 0) {
            setResolvedPricing(ratesApplied);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${Utils.baseurl}public/plans`);
                const r = await res.json();
                if (cancelled || !r.status) return;
                const growth = (r.data || []).find((p) => Number(p.plan_type) === PLAN_TYPE_CODES[1].value);
                setResolvedPricing(growth?.pricing || r.pricing || []);
            } catch { /* keep empty */ }
        })();
        return () => { cancelled = true; };
    }, [ratesApplied]);

    // Live usage counts for reference — e.g. "Mobile Push (4)"
    useEffect(() => {
        const uid = invoice.user?.id ?? invoice.user_id;
        if (!uid) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${Utils.baseurl}admin/billing/usage`, {
                    method: 'POST',
                    headers: authJsonHeaders(Utils.storageKeys.adminToken),
                    body: JSON.stringify({ userId: uid }),
                });
                const r = await res.json();
                if (cancelled || !r.status) return;
                const row = (r.data || [])[0];
                const byType = {};
                (row?.live_display_metrics || row?.current_metrics || []).forEach((m) => {
                    byType[m.type] = m.count ?? 0;
                });
                setActualCounts(byType);
            } catch { /* keep empty — label shows 0 */ }
        })();
        return () => { cancelled = true; };
    }, [invoice.user?.id, invoice.user_id]);

    const canCalculateLive = resolvedPricing.length > 0;

    const liveEstimate = useMemo(() => {
        if (!canCalculateLive) return null;
        // Same as Plan.js calculator: count × rate (no free-limit subtraction on admin edit)
        return calculateGrowthPlanEstimate({
            pricing: resolvedPricing,
            freeLimits: [],
            usageByType: counts,
            billingCycle,
        });
    }, [counts, resolvedPricing, billingCycle, canCalculateLive]);

    // If the invoice had no saved base amount, use the plan's base rate once pricing resolves
    // (only while the admin hasn't already typed their own base amount).
    useEffect(() => {
        if (baseTouched) return;
        if (Number.isFinite(parseFloat(estimate.base))) return;
        if (liveEstimate) setBaseAmount(liveEstimate.base || 0);
    }, [liveEstimate, baseTouched, estimate]);

    const variableTotal = liveEstimate?.variable ?? parseFloat(estimate.variable) ?? 0;
    const computedTotal = (canCalculateLive || countsTouched || baseTouched)
        ? (Number(baseAmount) || 0) + variableTotal
        : parseFloat(estimate.subtotal_before_tax ?? estimate.total ?? invoice.amount ?? 0);
    const gstPercent = parseFloat(breakdown.gst_percent) || 0;

    // Total input follows base/metric changes (base + count × rate); admin can still type a custom total
    useEffect(() => {
        if (!countsTouched && !baseTouched) return;
        setOverrideAmount(String(Math.round((Number(computedTotal) || 0) * 100) / 100));
    }, [computedTotal, countsTouched, baseTouched]);

    const updateCount = (type, raw) => {
        setCountsTouched(true);
        setCounts((c) => ({ ...c, [type]: Math.max(0, parseInt(raw, 10) || 0) }));
    };

    const updateBase = (raw) => {
        setBaseTouched(true);
        setBaseAmount(Math.max(0, parseFloat(raw) || 0));
    };

    const rateByType = useMemo(() => {
        const map = {};
        (resolvedPricing || []).forEach((row) => {
            const t = Number(row.type);
            if (t > 0) map[t] = parseFloat(row.price) || 0;
        });
        return map;
    }, [resolvedPricing]);

    const metricRows = useMemo(() => {
        const fromPricing = (resolvedPricing || []).filter((row) => Number(row.type) > 0);
        if (fromPricing.length > 0) {
            return fromPricing.map((row) => ({
                type: Number(row.type),
                label: row.type_label || METRIC_TYPE_CODES.find((m) => m.value === Number(row.type))?.label || `Metric ${row.type}`,
                rate: parseFloat(row.price) || 0,
            }));
        }
        return (METRIC_TYPE_CODES || []).filter((m) => m.value > 0).map((m) => ({
            type: m.value,
            label: m.label,
            rate: 0,
        }));
    }, [resolvedPricing]);

    const save = async () => {
        setLoading(true);
        const body = {
            invoiceId: invoice.id,
            usageByType: counts,
            baseAmount: Number(baseAmount) || 0,
        };
        // Total field always holds the final subtotal (auto from metrics or typed by admin)
        if (overrideAmount !== '') body.overrideAmount = parseFloat(overrideAmount) || 0;
        try {
            const res = await fetch(`${Utils.baseurl}admin/invoice/update-amount`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify(body),
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) { toast.success(r.message || 'Invoice updated'); onSuccess(); onClose(); }
            else toast.error(r.message || 'Could not update invoice');
        } catch (e) { toast.error(e?.message || 'Could not update invoice'); }
        setLoading(false);
    };

    return (
        <Modal title={`Edit Invoice #${invoice.id} — Amount`} onClose={onClose} maxWidth="700px"
            footer={<>
                <button type="button" onClick={onClose} style={{ padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button type="button" onClick={save} disabled={loading} style={{ padding: '10px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                    {loading ? 'Saving…' : 'Save changes'}
                </button>
            </>}
        >
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
                Edit billable usage for this invoice. Rates are from this invoice&apos;s saved pricing snapshot. Numbers in parentheses are live usage today.
            </p>

            {/* Base / fixed plan price — editable on its own, independent of the usage metrics below */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #f3f4f6' }}>
                <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
                    Base plan price
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>(fixed)</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: 13, color: '#374151' }}>{currSym}</span>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={baseAmount}
                        onChange={(e) => updateBase(e.target.value)}
                        style={{ width: 110, padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, textAlign: 'right', outline: 'none' }}
                    />
                </div>
            </div>

            {/* Metric inputs — rate before box; line total from count × invoice rate */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {metricRows.map((m) => {
                    const actualCount = actualCounts[m.type] ?? 0;
                    const count = counts[m.type] ?? 0;
                    const unitPrice = rateByType[m.type] ?? m.rate ?? 0;
                    const lineCost = count * unitPrice;
                    return (
                    <div key={m.type} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto', gap: 10, alignItems: 'center' }}>
                        <label style={{ fontSize: 13, color: '#374151' }}>
                            {m.label}
                            <span style={{ marginLeft: 6, fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>(live {Number(actualCount).toLocaleString()})</span>
                        </label>
                        <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {formatMetricPrice(currSym, unitPrice)}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={count}
                                onChange={(e) => updateCount(m.type, e.target.value)}
                                style={{ width: 88, padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, textAlign: 'right', outline: 'none' }}
                            />
                            <span style={{ fontSize: 12, color: '#4f46e5', fontWeight: 600, minWidth: 88, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                = {bigAmountFormat(lineCost, currSym)}
                            </span>
                        </div>
                    </div>
                    );
                })}
            </div>

            {/* Total amount — auto from metrics above, editable */}
            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 14}}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Total amount{gstPercent > 0 ? ` (before ${gstPercent}% GST)` : ''}
                </label>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af' }}>
                    Updates automatically when you change the metrics above. You can also type a custom total.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16, color: '#374151' }}>{currSym}</span>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={overrideAmount}
                        onChange={(e) => setOverrideAmount(e.target.value)}
                        style={{ flex: 1, padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, fontWeight: 600, outline: 'none' }}
                    />
                </div>

                {/* Live GST + grand total preview — follows the amount above as it's edited,
                    using this invoice's own gst_percent snapshot (same rate the backend applies on save). */}
                {gstPercent > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e5e7eb' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', padding: '2px 0' }}>
                            <span>Subtotal:</span>
                            <span>{bigAmountFormat(parseFloat(overrideAmount) || 0, currSym)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', padding: '2px 0' }}>
                            <span>GST ({gstPercent}%):</span>
                            <span>{bigAmountFormat(Math.round(((parseFloat(overrideAmount) || 0) * gstPercent / 100) * 100) / 100, currSym)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: '#1a1a1a', padding: '4px 0 0' }}>
                            <span>Total (with GST):</span>
                            <span style={{ color: '#4f46e5' }}>
                                {bigAmountFormat(
                                    Math.round(((parseFloat(overrideAmount) || 0) + (parseFloat(overrideAmount) || 0) * gstPercent / 100) * 100) / 100,
                                    currSym
                                )}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminBilling = () => {
    const pageSize = getDefaultPageSize();
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [kindFilter, setKindFilter] = useState('all');
    const [planFilter, setPlanFilter] = useState('');
    const [hasErrorsOnly, setHasErrorsOnly] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [datePreset, setDatePreset] = useState('');
    const [planOptions, setPlanOptions] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: pageSize, total: 0, totalPages: 0 });

    const [markPaidModal, setMarkPaidModal] = useState(null);
    const [editAmountModal, setEditAmountModal] = useState(null);
    const [approveModal, setApproveModal] = useState(null);
    const [viewModal, setViewModal] = useState(null);
    const [paymentErrorsInvoice, setPaymentErrorsInvoice] = useState(null);
    const [paymentErrors, setPaymentErrors] = useState([]);

    const invoiceTableScrollRef = useRef(null);
    const {
        openActionItem,
        dropdownPosition,
        dropdownRef,
        handleDropdownAction,
        handleOpenDropdown,
    } = useActionDropdown({
        tableScrollRef: invoiceTableScrollRef,
        optionCount: (row) => getInvoiceActionOptions(row, {
            onView: () => {},
            onMarkPaid: () => {},
            onEdit: () => {},
            onPaymentErrors: () => {},
            onApprove: () => {},
        }).length,
        menuWidth: 200,
        useAnchored: true,
    });

    const openPaymentErrors = (invoice) => {
        setPaymentErrorsInvoice(invoice);
        setPaymentErrors(getInvoicePaymentAttempts(invoice));
    };

    const resetToFirstPage = () => setPagination((p) => ({ ...p, page: 1 }));

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${Utils.baseurl}public/plans`);
                const r = await res.json();
                if (cancelled || !r.status) return;
                setPlanOptions((r.data || []).map((p) => ({ value: p.id, label: p.name })).filter((p) => p.value));
            } catch { /* optional filter */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}admin/billing/invoices`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({
                    page: pagination.page,
                    limit: pagination.limit || pageSize,
                    search,
                    status: statusFilter,
                    invoice_kind: kindFilter,
                    plan_id: planFilter || undefined,
                    has_payment_errors: hasErrorsOnly || undefined,
                    date_from: dateFrom || undefined,
                    date_to: dateTo || undefined,
                    timezone: getBrowserTimeZone() || undefined,
                }),
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) {
                setInvoices(r.data || []);
                setPagination((p) => ({ ...p, ...r.pagination, limit: r.pagination?.limit || p.limit }));
            } else toast.error(r.message || 'Could not load invoices');
        } catch (e) { toast.error(e?.message || 'Could not load invoices'); }
        setLoading(false);
    }, [pagination.page, pagination.limit, search, statusFilter, kindFilter, planFilter, hasErrorsOnly, dateFrom, dateTo, pageSize]);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

    const statusSelectValue = useMemo(
        () => INVOICE_STATUS_OPTIONS.find((o) => String(o.value) === String(statusFilter)) || INVOICE_STATUS_OPTIONS[0],
        [statusFilter]
    );
    const kindSelectValue = useMemo(
        () => KIND_OPTIONS.find((o) => o.value === kindFilter) || KIND_OPTIONS[0],
        [kindFilter]
    );
    const planSelectValue = useMemo(() => {
        if (planFilter === '' || planFilter == null) return null;
        return planOptions.find((o) => String(o.value) === String(planFilter)) || null;
    }, [planOptions, planFilter]);
    const errorSelectValue = useMemo(
        () => ERROR_OPTIONS.find((o) => o.value === (hasErrorsOnly ? 'errors' : 'all')) || ERROR_OPTIONS[0],
        [hasErrorsOnly]
    );

    const handleDateRangeChange = ({ dateFrom: from, dateTo: to, preset }) => {
        setDateFrom(from || '');
        setDateTo(to || '');
        setDatePreset(preset || '');
        resetToFirstPage();
    };

    const S = {
        th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #f3f4f6', background: '#f9fafb' },
        td: { padding: '12px 16px', fontSize: 13, color: '#374151', borderBottom: '1px solid #f9fafb', verticalAlign: 'top' },
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: '#111827' }}>Billing</h1>
                    <p style={{ color: '#6b7280', marginTop: 6, fontSize: 14 }}>Invoices across all platform users</p>
                </div>
                <button type="button" onClick={fetchInvoices}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                    <MdRefresh size={16} /> Refresh
                </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
                <div style={{ minWidth: 180, width: 200 }}>
                    <Select
                        inputId="admin-billing-status"
                        instanceId="admin-billing-status"
                        classNamePrefix="ns-select"
                        isSearchable
                        options={INVOICE_STATUS_OPTIONS}
                        value={statusSelectValue}
                        onChange={(opt) => { setStatusFilter(opt?.value ?? 'all'); resetToFirstPage(); }}
                        styles={filterSelectStyles}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        placeholder="Status"
                    />
                </div>
                <div style={{ minWidth: 180, width: 200 }}>
                    <Select
                        inputId="admin-billing-type"
                        instanceId="admin-billing-type"
                        classNamePrefix="ns-select"
                        isSearchable
                        options={KIND_OPTIONS}
                        value={kindSelectValue}
                        onChange={(opt) => { setKindFilter(opt?.value ?? 'all'); resetToFirstPage(); }}
                        styles={filterSelectStyles}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        placeholder="Type"
                    />
                </div>
                <div style={{ minWidth: 180, width: 200 }}>
                    <Select
                        inputId="admin-billing-plan"
                        instanceId="admin-billing-plan"
                        classNamePrefix="ns-select"
                        isSearchable
                        isClearable
                        options={planOptions}
                        value={planSelectValue}
                        onChange={(opt) => { setPlanFilter(opt?.value ?? ''); resetToFirstPage(); }}
                        styles={filterSelectStyles}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        placeholder="Plan"
                    />
                </div>
                <div style={{ minWidth: 180, width: 200 }}>
                    <Select
                        inputId="admin-billing-errors"
                        instanceId="admin-billing-errors"
                        classNamePrefix="ns-select"
                        isSearchable={false}
                        options={ERROR_OPTIONS}
                        value={errorSelectValue}
                        onChange={(opt) => { setHasErrorsOnly(opt?.value === 'errors'); resetToFirstPage(); }}
                        styles={filterSelectStyles}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        placeholder="Errors"
                    />
                </div>
                <div style={{ minWidth: 220 }}>
                    <DateRangePicker
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        preset={datePreset}
                        onChange={handleDateRangeChange}
                        label="Invoice date"
                        placeholder="Invoice date"
                        allowClear
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', padding: '8px 14px', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginLeft: 'auto' }}>
                    <MdSearch size={16} color="#9ca3af" />
                    <input type="text" placeholder="Search user email..." value={search}
                        onChange={(e) => { setSearch(e.target.value); resetToFirstPage(); }}
                        style={{ border: 'none', outline: 'none', fontSize: 13, width: 200 }} />
                </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div ref={invoiceTableScrollRef} style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
                        <thead>
                            <tr>
                                <th style={S.th}>User</th>
                                <th style={S.th}>Type</th>
                                <th style={S.th}>Plan</th>
                                <th style={S.th}>Billing Cycle</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>Amount</th>
                                <th style={S.th}>Status</th>
                                <th style={S.th}>Error</th>
                                <th style={S.th}>Date</th>
                                <th style={{ ...S.th, textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</td></tr>
                            ) : invoices.length === 0 ? (
                                <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', padding: 40, color: '#9ca3af' }}>No records</td></tr>
                            ) : invoices.map((row) => {
                                const isPaid = Number(row.status) === INVOICE_PAID;
                                const statusUi = getInvoiceStatusDisplay(row.status);
                                const kindUi = getInvoiceKindDisplay(row.invoice_kind);
                                const showError = !isPaid && row.has_payment_errors && row.last_payment_error;
                                const errText = showError ? invoicePaymentErrorText(row) : '';
                                return (
                                <tr key={row.id || row.invoice_id}>
                                    <td style={S.td}>
                                        <div style={{ fontWeight: 500 }}>{row.user?.name || '—'}</div>
                                        <div style={{ fontSize: 12, color: '#9ca3af' }}>{row.user?.email}</div>
                                    </td>
                                    <td style={S.td}>
                                        <span className={kindUi.badgeClass || 'badge rounded-pill bg-light-secondary'}>
                                            {kindUi.label}
                                        </span>
                                    </td>
                                    <td style={S.td}>{row.plan_name || '—'}</td>
                                    <td style={{ ...S.td, color: '#6b7280', whiteSpace: 'nowrap' }}>
                                        {isCheckoutInvoice(row) ? '—' : `${formatDate(row.period_start)} – ${formatDate(row.period_end)}`}
                                        {(row.usage_breakdown?.billing_cycle || row.billing_cycle) ? (
                                            <div style={{ fontSize: 11, marginTop: 2, textTransform: 'capitalize' }}>
                                                {row.usage_breakdown?.billing_cycle || row.billing_cycle}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{bigAmountFormat(row.amount, row.currency_symbol)}</td>
                                    <td style={S.td}>
                                        <span className={statusUi.badgeClass || 'badge rounded-pill bg-light-secondary'}>
                                            {statusUi.label}
                                        </span>
                                        {isAwaitingAdminApproval(row) && (
                                            <div style={{ fontSize: 11, color: '#b45309', marginTop: 4, whiteSpace: 'nowrap' }}>
                                                {/* Manual approval mode (for now): user_visible_at is a far-future
                                                    placeholder until an admin approves, so there's no real reveal
                                                    date to show here yet. */}
                                                Admin review · awaiting manual approval
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ ...S.td, maxWidth: 260 }}>
                                        {showError ? (
                                            <span
                                                style={{ fontSize: 12, color: '#b91c1c', lineHeight: 1.35, display: 'inline-block' }}
                                                title={row.last_payment_error.error_message || row.last_payment_error.error_description || errText}
                                            >
                                                {errText.length > 90 ? `${errText.slice(0, 90)}…` : errText}
                                            </span>
                                        ) : (
                                            <span style={{ color: '#9ca3af' }}>—</span>
                                        )}
                                    </td>
                                    <td style={{ ...S.td, fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                                        {formatDateTime(row.date || row.paid_at || row.createdAt)}
                                    </td>
                                    <td style={{ ...S.td, textAlign: 'center' }}>
                                        <button
                                            type="button"
                                            className="dropdown-trigger-btn btn btn-link p-0"
                                            aria-label="Invoice actions"
                                            onClick={(e) => handleOpenDropdown(e, row.id, row)}
                                        >
                                            <BsThreeDotsVertical size={16} />
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <ListPaginationBar
                    page={pagination.page}
                    setPage={(n) => setPagination((p) => ({ ...p, page: n }))}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    totalPages={pagination.totalPages}
                    ariaLabel="Admin invoices pagination"
                    className="px-3 py-3"
                    style={{ borderTop: '1px solid #f3f4f6' }}
                    loading={loading}
                />
            </div>

            {markPaidModal && (
                <MarkPaidModal invoice={markPaidModal} onClose={() => setMarkPaidModal(null)} onSuccess={fetchInvoices} />
            )}

            {editAmountModal && (
                <EditAmountModal invoice={editAmountModal} onClose={() => setEditAmountModal(null)} onSuccess={fetchInvoices} />
            )}

            {approveModal && (
                <ApproveInvoiceModal invoice={approveModal} onClose={() => setApproveModal(null)} onSuccess={fetchInvoices} />
            )}

            {viewModal && (
                <Modal title={`Invoice #${viewModal.id}`} onClose={() => setViewModal(null)} maxWidth="640px"
                    footer={<button type="button" onClick={() => setViewModal(null)} style={{ padding: '10px 18px', background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#374151' }}>Close</button>}
                >
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                            {[
                                ['User', viewModal.user?.name || viewModal.user?.email || '—'],
                                ['Amount', bigAmountFormat(viewModal.amount, viewModal.currency_symbol)],
                                ['Type', getInvoiceKindDisplay(viewModal.invoice_kind).label],
                                ['Plan', viewModal.plan_name || '—'],
                                ['Status', viewModal.status_label || '—'],
                                ['Paid', viewModal.paid_at ? formatDateTime(viewModal.paid_at) : '—'],
                            ].map(([label, val]) => (
                                <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 12px' }}>
                                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>{label}</div>
                                    <strong style={{ fontSize: 13, color: '#111827', wordBreak: 'break-all' }}>{val}</strong>
                                </div>
                            ))}
                        </div>
                        {viewModal.has_payment_errors && viewModal.last_payment_error ? (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#991b1b' }}>
                                <div style={{ fontSize: 11, color: '#b91c1c', fontWeight: 600, marginBottom: 4 }}>Payment error</div>
                                {invoicePaymentErrorText(viewModal)}
                            </div>
                        ) : null}
                        {!isCheckoutInvoice(viewModal) && (
                            <div style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>
                                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Period</div>
                                <strong>{formatDate(viewModal.period_start)} – {formatDate(viewModal.period_end)}</strong>
                            </div>
                        )}

                        <BillView
                            breakdown={viewModal.usage_breakdown}
                            invoiceAmount={viewModal.amount}
                            currencySymbol={viewModal.currency_symbol}
                            planName={viewModal.plan_name}
                            invoiceKind={viewModal.invoice_kind}
                        />
                </Modal>
            )}

            {openActionItem && dropdownPosition && createPortal(
                <div
                    ref={dropdownRef}
                    style={{ position: 'fixed', left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 10000, minWidth: 200 }}
                >
                    <div className="dropdown-menu-subscription">
                        {getInvoiceActionOptions(openActionItem, {
                            onView: setViewModal,
                            onMarkPaid: setMarkPaidModal,
                            onEdit: setEditAmountModal,
                            onPaymentErrors: openPaymentErrors,
                            onApprove: setApproveModal,
                        }).map((opt) => (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => handleDropdownAction(opt.action)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <span style={{ marginRight: 8, flexShrink: 0, display: 'flex' }}>{opt.icon}</span>
                                <span style={{ fontSize: '0.875rem' }}>{opt.label}</span>
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}

            {paymentErrorsInvoice && (
                <Modal
                    title={`Payment errors — Invoice #${paymentErrorsInvoice.id}`}
                    onClose={() => { setPaymentErrorsInvoice(null); setPaymentErrors([]); }}
                    maxWidth="720px"
                    footer={(
                        <button
                            type="button"
                            onClick={() => { setPaymentErrorsInvoice(null); setPaymentErrors([]); }}
                            style={{ padding: '10px 18px', background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#374151' }}
                        >
                            Close
                        </button>
                    )}
                >
                    {paymentErrors.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>No payment attempts recorded yet.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {paymentErrors.map((a, idx) => (
                                <div
                                    key={`${a.order_id || 'ord'}-${a.payment_id || 'pay'}-${a.at || idx}`}
                                    style={{ border: '1px solid #fee2e2', background: '#fff7f7', borderRadius: 10, padding: '12px 14px' }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                                        <strong style={{ fontSize: 13, color: '#991b1b' }}>
                                            {a.error_reason || a.error_code || a.razorpay_status || 'Failed'}
                                        </strong>
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                                            {a.at ? formatDateTime(a.at) : '—'}
                                            {a.source ? ` · ${a.source}` : ''}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                                        <div><span style={{ color: '#9ca3af' }}>Error code:</span> {a.error_code || '—'}</div>
                                        <div><span style={{ color: '#9ca3af' }}>Reason:</span> {a.error_reason || '—'}</div>
                                        <div><span style={{ color: '#9ca3af' }}>Message:</span> {a.error_description || a.error_message || '—'}</div>
                                        <div><span style={{ color: '#9ca3af' }}>Order ID:</span> <span style={{ wordBreak: 'break-all' }}>{a.order_id || '—'}</span></div>
                                        <div><span style={{ color: '#9ca3af' }}>Payment ID:</span> <span style={{ wordBreak: 'break-all' }}>{a.payment_id || '—'}</span></div>
                                        {a.razorpay_status && (
                                            <div><span style={{ color: '#9ca3af' }}>Razorpay status:</span> {a.razorpay_status}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
};

export default AdminBilling;
